import { useEffect, useState } from "react";
import { listOrders, createOrder, updateOrder, deleteOrder, importOrdersCsv, getSettings } from "@/lib/api";
import { Plus, ArrowSquareOut, Trash, UploadSimple, X, MagnifyingGlass, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUSES = ["placed", "processed", "shipped", "delivered", "cancelled"];

const StatusPill = ({ status, onChange, testId }) => {
  const styles = {
    placed: "bg-arena-warning text-black",
    processed: "bg-arena-primary text-black",
    shipped: "bg-blue-400 text-black",
    delivered: "bg-arena-success text-black",
    cancelled: "bg-arena-danger text-white",
  };
  return (
    <select
      data-testid={testId}
      value={status}
      onChange={(e) => onChange(e.target.value)}
      className={`px-2 py-1 text-[10px] tracking-widest font-bold uppercase border-none outline-none cursor-pointer ${styles[status]}`}
    >
      {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
    </select>
  );
};

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    order_number: "", customer_name: "", customer_email: "",
    product_name: "", product_sku: "", quantity: 1, price: 0,
    shipping_address: "", notes: ""
  });

  const load = async () => {
    const [o, s] = await Promise.all([listOrders(filter || undefined), getSettings()]);
    setOrders(o);
    setSettings(s);
  };

  useEffect(() => { load(); }, [filter]);

  const filtered = orders.filter(o => {
    if (!search) return true;
    const q = search.toLowerCase();
    return o.order_number.toLowerCase().includes(q) ||
           o.customer_name.toLowerCase().includes(q) ||
           o.product_name.toLowerCase().includes(q);
  });

  const handleCreate = async () => {
    if (!form.order_number || !form.customer_name || !form.product_name) {
      toast.error("Order#, customer, and product are required");
      return;
    }
    try {
      await createOrder({ ...form, quantity: parseInt(form.quantity), price: parseFloat(form.price) });
      toast.success("Order added");
      setShowCreate(false);
      setForm({ order_number: "", customer_name: "", customer_email: "", product_name: "", product_sku: "", quantity: 1, price: 0, shipping_address: "", notes: "" });
      load();
    } catch (e) {
      toast.error("Failed to create order");
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await updateOrder(id, { status });
      toast.success(`Status → ${status.toUpperCase()}`);
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this order?")) return;
    await deleteOrder(id);
    toast.success("Order deleted");
    load();
  };

  const handleCsvImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await importOrdersCsv(file);
      toast.success(`Imported ${res.inserted} orders${res.errors.length ? ` (${res.errors.length} errors)` : ''}`);
      if (res.errors.length) console.warn("CSV import errors:", res.errors);
      load();
    } catch (e) {
      toast.error("Import failed");
    } finally {
      e.target.value = "";
    }
  };

  const processOnSellvia = (order) => {
    const url = settings?.sellvia_dashboard_url || "https://app.sellvia.com/";
    window.open(url, "_blank", "noopener");
    // copy order details to clipboard for pasting
    const details = `Order: ${order.order_number}\nCustomer: ${order.customer_name}\nEmail: ${order.customer_email}\nProduct: ${order.product_name} (SKU: ${order.product_sku})\nQty: ${order.quantity}\nAddress: ${order.shipping_address}`;
    navigator.clipboard?.writeText(details);
    toast.success("Order details copied — paste into Sellvia");
  };

  return (
    <div className="p-8" data-testid="orders-page">
      {/* Header */}
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="overline mb-2">// order processing</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">ORDER <span className="text-arena-primary">QUEUE</span></h1>
          <p className="text-arena-muted mt-2 text-sm">Track, process, and deep-link into Sellvia. Click PROCESS to open Sellvia + auto-copy order details.</p>
        </div>
        <div className="flex gap-2">
          <label className="border border-arena-border px-4 py-2 text-xs tracking-widest uppercase cursor-pointer hover:bg-arena-surfaceHover hover:border-arena-primary flex items-center gap-2" data-testid="import-csv-label">
            <UploadSimple size={14} weight="bold" />
            Import CSV
            <input type="file" accept=".csv" className="hidden" onChange={handleCsvImport} data-testid="import-csv-input" />
          </label>
          <button
            onClick={() => setShowCreate(true)}
            data-testid="new-order-btn"
            className="bg-arena-primary text-black px-4 py-2 text-xs tracking-widest uppercase font-bold hover:bg-arena-primaryHover flex items-center gap-2"
          >
            <Plus size={14} weight="bold" />
            New Order
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 border border-arena-border bg-arena-surface p-3">
        <div className="flex items-center gap-2 flex-1 border border-arena-border px-3 py-2">
          <MagnifyingGlass size={14} className="text-arena-muted" />
          <input
            data-testid="search-input"
            placeholder="Search orders, customers, products..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="bg-transparent flex-1 outline-none text-xs text-arena-text"
          />
        </div>
        <div className="flex gap-1">
          <button
            data-testid="filter-all"
            onClick={() => setFilter("")}
            className={`px-3 py-2 text-[10px] tracking-widest uppercase ${!filter ? 'bg-arena-primary text-black font-bold' : 'text-arena-muted hover:text-arena-primary'}`}
          >
            all
          </button>
          {STATUSES.map(s => (
            <button
              key={s}
              data-testid={`filter-${s}`}
              onClick={() => setFilter(s)}
              className={`px-3 py-2 text-[10px] tracking-widest uppercase ${filter === s ? 'bg-arena-primary text-black font-bold' : 'text-arena-muted hover:text-arena-primary'}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="border border-arena-border bg-arena-surface overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-arena-border text-arena-muted overline">
              <th className="text-left px-4 py-3">Order#</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">Product</th>
              <th className="text-right px-4 py-3">Qty</th>
              <th className="text-right px-4 py-3">Total</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-16 text-arena-muted">
                No orders. Click <span className="text-arena-primary">New Order</span> or import a CSV.
              </td></tr>
            )}
            {filtered.map(o => (
              <tr key={o.id} className="border-b border-arena-border hover:bg-arena-surfaceHover" data-testid={`order-row-${o.order_number}`}>
                <td className="px-4 py-3 font-heading text-arena-primary">{o.order_number}</td>
                <td className="px-4 py-3">
                  <div>{o.customer_name}</div>
                  <div className="text-arena-muted text-[10px]">{o.customer_email}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="truncate max-w-[220px]">{o.product_name}</div>
                  {o.product_sku && <div className="text-arena-muted text-[10px]">SKU: {o.product_sku}</div>}
                </td>
                <td className="px-4 py-3 text-right">{o.quantity}</td>
                <td className="px-4 py-3 text-right text-arena-primary font-bold">${(o.price * o.quantity).toFixed(2)}</td>
                <td className="px-4 py-3">
                  <StatusPill status={o.status} onChange={(s) => handleStatusChange(o.id, s)} testId={`status-select-${o.order_number}`} />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => processOnSellvia(o)}
                      data-testid={`process-btn-${o.order_number}`}
                      className="bg-arena-primary text-black px-2 py-1.5 text-[10px] tracking-widest uppercase font-bold hover:bg-arena-primaryHover flex items-center gap-1"
                      title="Open Sellvia and copy details"
                    >
                      <ArrowSquareOut size={12} weight="bold" />
                      Process
                    </button>
                    <button
                      onClick={() => { navigator.clipboard?.writeText(o.shipping_address); toast.success("Address copied"); }}
                      className="text-arena-muted hover:text-arena-primary p-1.5"
                      title="Copy shipping address"
                      data-testid={`copy-addr-${o.order_number}`}
                    >
                      <Copy size={14} weight="bold" />
                    </button>
                    <button
                      onClick={() => handleDelete(o.id)}
                      data-testid={`delete-${o.order_number}`}
                      className="text-arena-danger hover:text-red-400 p-1.5"
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* CSV Format hint */}
      <div className="mt-4 border border-arena-border bg-arena-surface p-4 text-[11px] text-arena-muted">
        <span className="overline text-arena-primary">// csv format</span>
        <code className="ml-2">order_number, customer_name, customer_email, product_name, product_sku, quantity, price, shipping_address, notes</code>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-arena-surface border border-arena-border w-full max-w-2xl" onClick={e => e.stopPropagation()} data-testid="new-order-modal">
            <div className="flex items-center justify-between p-5 border-b border-arena-border">
              <h2 className="font-heading text-xl font-bold">NEW ORDER</h2>
              <button onClick={() => setShowCreate(false)} className="text-arena-muted hover:text-arena-danger" data-testid="close-modal">
                <X size={18} weight="bold" />
              </button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-4">
              {[
                { k: "order_number", label: "Order #", req: true },
                { k: "customer_name", label: "Customer Name", req: true },
                { k: "customer_email", label: "Customer Email" },
                { k: "product_name", label: "Product", req: true },
                { k: "product_sku", label: "SKU" },
                { k: "quantity", label: "Quantity", type: "number" },
                { k: "price", label: "Price ($)", type: "number", step: "0.01" },
                { k: "shipping_address", label: "Shipping Address", full: true },
                { k: "notes", label: "Notes", full: true },
              ].map(f => (
                <div key={f.k} className={f.full ? "col-span-2" : ""}>
                  <label className="overline block mb-1">{f.label}{f.req && <span className="text-arena-danger"> *</span>}</label>
                  <input
                    data-testid={`input-${f.k}`}
                    type={f.type || "text"}
                    step={f.step}
                    value={form[f.k]}
                    onChange={e => setForm({ ...form, [f.k]: e.target.value })}
                    className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-xs text-arena-text focus:border-arena-primary outline-none"
                  />
                </div>
              ))}
            </div>
            <div className="p-5 border-t border-arena-border flex justify-end gap-2">
              <button onClick={() => setShowCreate(false)} className="border border-arena-border px-4 py-2 text-xs tracking-widest uppercase hover:bg-arena-surfaceHover">Cancel</button>
              <button onClick={handleCreate} data-testid="submit-new-order" className="bg-arena-primary text-black px-4 py-2 text-xs tracking-widest uppercase font-bold hover:bg-arena-primaryHover">Create Order</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
